import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  CreditCard,
  Quote,
  Store,
  Zap,
} from "lucide-react";
import { RotatingHero } from "@/components/RotatingHero";
import { EditorialMarquee } from "@/components/Marquee";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Stat } from "@/components/Stat";
import { CTABand } from "@/components/PageHero";
import { faqs, products, services } from "@/data/products";


const img = (id, w = 1000) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

const pillars = [
  {
    icon: CreditCard,
    title: "Move Money",
    copy: "Secure payment gateways with instant settlement and trackable transactions.",
  },
  {
    icon: Store,
    title: "Run Smarter",
    copy: "Affordable POS hardware and software — inventory, ordering and engagement in one place.",
  },
  {
    icon: Zap,
    title: "Grow Faster",
    copy: "VAS income streams, bulk-buying power and funding-ready compliance support.",
  },
];

const testimonials = [
  {
    quote:
      "DAS set up our POS in a day and trained the whole team. Card sales now settle instantly — cash flow has never been this clear.",
    name: "Thandi M.",
    role: "Spaza Shop Owner, Soweto",
  },
  {
    quote:
      "Selling airtime and electricity from the same till point added a whole new income stream for my store.",
    name: "Sipho N.",
    role: "Retailer, Tembisa",
  },
  {
    quote:
      "The rental model meant we didn't need big capital to go digital. Their support team actually picks up the phone.",
    name: "Karabo L.",
    role: "Café Owner, Gaborone",
  },
];

const FaqItem = ({ item, open, onToggle, index }) => (
  <div className="border-b border-neutral-200" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      data-testid={`faq-toggle-${index}`}
      className="flex w-full items-center justify-between gap-6 py-6 text-left"
    >
      <span className="font-display text-lg font-semibold tracking-tight text-das-ink sm:text-xl">
        {item.q}
      </span>
      <motion.span
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ duration: 0.35 }}
        className={`grid h-10 w-10 shrink-0 place-items-center rounded-full border transition-colors ${
          open ? "border-das-violet bg-das-violet text-white" : "border-neutral-300 text-das-ink"
        }`}
      >
        <ChevronDown className="h-4 w-4" />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="max-w-3xl pb-7 text-sm leading-relaxed text-neutral-600 sm:text-base">
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);
  const featured = products.filter((p) => p.featured);

  return (
    <div data-testid="home-page">
      <RotatingHero />
      <EditorialMarquee />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-das-deep">
                DAS in action
              </p>
            </Reveal>
            <h2 className="mt-5 font-display text-4xl font-medium tracking-tight text-das-ink sm:text-5xl">
              <MaskedLine delay={0.1}>Watch how we power</MaskedLine>
              <MaskedLine delay={0.2}>
                the <span className="italic text-das-violet">new generation</span>
              </MaskedLine>
            </h2>
            <Reveal delay={0.25}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-neutral-600">
                Press play to see how Dynamic App Solutions helps small businesses accept payments,
                manage sales and grow — from the corner shop to the cloud.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <Link
                to="/consultation"
                data-testid="video-section-cta"
                className="group mt-8 inline-flex items-center gap-2 font-display text-base font-semibold text-das-deep"
              >
                Start your journey
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="relative">
              <div className="pointer-events-none absolute -left-8 -top-8 h-32 w-32 rounded-full bg-das-violet" />
              <div
                className="relative overflow-hidden rounded-[2rem] border border-neutral-200 bg-das-ink shadow-lift"
                data-testid="home-video-banner"
              >
                <video
                  data-testid="home-promo-video"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster="/assets/das-promo-poster.jpg"
                  preload="metadata"
                  className="aspect-video w-full object-cover"
                >
                  <source src="/assets/das-promo.webm" type="video/webm" />
                  <source src="/assets/das-promo.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      <section className="mx-auto max-w-7xl px-6 py-28 md:px-12">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-das-deep">
                Who we are
              </p>
            </Reveal>
            <h2 className="mt-6 font-display text-4xl font-medium leading-[1.12] tracking-tight text-das-ink sm:text-5xl">
              <MaskedLine delay={0.1}>A 100% black-owned fintech</MaskedLine>
              <MaskedLine delay={0.2}>from Johannesburg, powering</MaskedLine>
              <MaskedLine delay={0.3}>
                the <span className="italic text-das-violet">new generation</span> of business.
              </MaskedLine>
            </h2>
          </div>
          <div className="flex flex-col justify-end lg:col-span-5">
            <Reveal delay={0.25}>
              <p className="text-base leading-relaxed text-neutral-600 sm:text-lg">
                Dynamic App Solutions helps retail, e-commerce, tourism and township businesses move
                money, manage funds and grow digitally — across Gauteng, South Africa and Botswana.
              </p>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="group mt-8 inline-flex items-center gap-2 font-display text-base font-semibold text-das-deep"
              >
                Our story
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 grid gap-6 md:grid-cols-3">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div
                data-testid={`pillar-card-${i}`}
                className="group h-full rounded-3xl border border-neutral-200 bg-white p-9 transition-all duration-500 hover:-translate-y-2 hover:border-das-violet/40 hover:shadow-lift"
              >
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-das-violet/10 text-das-deep transition-colors duration-500 group-hover:bg-das-violet group-hover:text-white">
                  <p.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-7 font-display text-2xl font-semibold tracking-tight text-das-ink">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-28 md:px-12">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-das-deep">
                The toolkit
              </p>
            </Reveal>
            <h2 className="mt-5 font-display text-4xl font-medium tracking-tight text-das-ink sm:text-5xl">
              <MaskedLine delay={0.1}>Hardware & software that</MaskedLine>
              <MaskedLine delay={0.2}>work as hard as you do</MaskedLine>
            </h2>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/products"
              data-testid="home-products-link"
              className="group inline-flex items-center gap-2 rounded-full border border-neutral-300 px-6 py-3 font-display text-sm font-semibold text-das-ink transition-all duration-300 hover:border-das-violet hover:text-das-deep"
            >
              Browse all products
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-7" delay={0.05}>
            <Link
              to="/products"
              data-testid="featured-product-card-0"
              className="group relative block h-[26rem] overflow-hidden rounded-[2rem] border border-neutral-200"
            >
              <img
                src={featured[0].image}
                alt={featured[0].name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-das-ink/80 via-das-ink/10 to-transparent" />
              <div className="absolute bottom-0 p-9">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/70">
                  {featured[0].category}
                </p>
                <h3 className="mt-2 font-display text-3xl font-semibold tracking-tight text-white">
                  {featured[0].name}
                </h3>
                <p className="mt-1 text-sm text-white/75">{featured[0].tagline}</p>
              </div>
            </Link>
          </Reveal>
          <div className="grid gap-6 lg:col-span-5">
            {featured.slice(1).map((p, i) => (
              <Reveal key={p.id} delay={0.12 + i * 0.1}>
                <Link
                  to="/products"
                  data-testid={`featured-product-card-${i + 1}`}
                  className="group relative block h-[12.5rem] overflow-hidden rounded-[2rem] border border-neutral-200"
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-das-ink/80 via-das-ink/10 to-transparent" />
                  <div className="absolute bottom-0 p-7">
                    <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/70">
                      {p.category}
                    </p>
                    <h3 className="mt-1 font-display text-xl font-semibold tracking-tight text-white">
                      {p.name}
                    </h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-neutral-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 sm:grid-cols-2 md:px-12 lg:grid-cols-4">
          <Stat value={100} suffix="%" label="Black-Owned" testid="stat-black-owned" />
          <Stat value={2} suffix="" label="Countries Served" testid="stat-countries" delay={0.1} />
          <Stat value={6} suffix="" label="Service Pillars" testid="stat-services" delay={0.2} />
          <Stat value={15} suffix="+" label="Products in Range" testid="stat-products" delay={0.3} />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-28 md:px-12">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-das-deep">
            What we do
          </p>
        </Reveal>
        <h2 className="mt-5 max-w-2xl font-display text-4xl font-medium tracking-tight text-das-ink sm:text-5xl">
          <MaskedLine delay={0.1}>Six ways we move</MaskedLine>
          <MaskedLine delay={0.2}>your business forward</MaskedLine>
        </h2>

        <div className="mt-16 space-y-2">
          {services.slice(0, 4).map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.06}>
              <Link
                to="/services"
                data-testid={`home-service-row-${i}`}
                className="group flex items-center justify-between gap-6 border-t border-neutral-200 py-7 transition-colors duration-300 hover:border-das-violet"
              >
                <div className="flex items-baseline gap-6 sm:gap-10">
                  <span className="font-display text-sm font-bold text-das-violet">{s.number}</span>
                  <span className="font-display text-2xl font-semibold tracking-tight text-das-ink transition-transform duration-500 group-hover:translate-x-3 sm:text-3xl">
                    {s.title}
                  </span>
                </div>
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-neutral-300 text-das-ink transition-all duration-300 group-hover:border-das-violet group-hover:bg-das-violet group-hover:text-white">
                  <ArrowUpRight className="h-5 w-5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-das-ink py-28 text-white">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-das-violet/20" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-das-purple">
              Word on the street
            </p>
          </Reveal>
          <h2 className="mt-5 font-display text-4xl font-medium tracking-tight sm:text-5xl">
            <MaskedLine delay={0.1}>Businesses that bank on us</MaskedLine>
          </h2>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <div
                  data-testid={`testimonial-card-${i}`}
                  className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/5 p-9 backdrop-blur transition-all duration-500 hover:-translate-y-2 hover:border-das-violet/50"
                >
                  <Quote className="h-7 w-7 text-das-violet" />
                  <p className="mt-6 flex-1 text-sm leading-relaxed text-white/80">{t.quote}</p>
                  <div className="mt-8 border-t border-white/10 pt-5">
                    <p className="font-display text-base font-semibold">{t.name}</p>
                    <p className="mt-0.5 text-xs uppercase tracking-[0.16em] text-white/50">
                      {t.role}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-28 md:px-12">
        <Reveal>
          <p className="text-center text-xs font-bold uppercase tracking-[0.24em] text-das-deep">
            Questions, answered
          </p>
        </Reveal>
        <h2 className="mt-5 text-center font-display text-4xl font-medium tracking-tight text-das-ink sm:text-5xl">
          <MaskedLine delay={0.1}>Before you ask</MaskedLine>
        </h2>
        <Reveal delay={0.2}>
          <div className="mt-14 border-t border-neutral-200">
            {faqs.map((f, i) => (
              <FaqItem
                key={f.q}
                item={f}
                index={i}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </Reveal>
      </section>

      <CTABand
        title="Ready to power faster payments?"
        copy="Tell us about your business and we'll design a solution that fits — from a single POS device to a full payment platform."
        testid="home-cta-band"
      />
    </div>
  );
}
